import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, CalendarDays, ChevronDown, Clock, HeartHandshake, MapPin, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, fmtDate, rupiah } from "@/lib/api";
import { Chapter, MaskLine, Reveal, EASE } from "@/components/motion";
import { txt } from "@/lib/site";
import { CoverImage } from "@/components/CoverImage";

export function EventBadge({ e }) {
  const left = e.quota ? e.quota - (e.registrations || 0) : null;
  if (left !== null && left <= Math.max(3, e.quota * 0.2)) return <span className="text-[11px] font-bold bg-amber-100 text-amber-700 rounded-full px-3 py-1 shadow-sm">Kuota hampir penuh</span>;
  if (!e.price) return <span className="text-[11px] font-bold bg-emerald-500 text-white rounded-full px-3 py-1 shadow-sm">Gratis</span>;
  return <span className="text-[11px] font-bold bg-blue-100 text-blue-700 rounded-full px-3 py-1 shadow-sm">{rupiah(e.price)}</span>;
}

export function EventsStrip({ items = [], site }) {
  return (
    <section id="kegiatan" data-testid="events-section" className="py-24 md:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-end justify-between gap-6">
        <div>
          <Reveal><Chapter n="04" label={txt(site, "events_label", "Kegiatan Mendatang")} /></Reveal>
          <h2 className="mt-10 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[0.95]"><MaskLine inView>{txt(site, "events_heading", "Geser, temukan agenda.")}</MaskLine></h2>
        </div>
        <Link to="/portal/events" data-testid="events-see-all" className="hidden md:inline-flex items-center gap-2 font-bold text-blue-600 hover:text-blue-700">Daftar di Portal <ArrowRight className="w-4 h-4" /></Link>
      </div>
      <div className="mt-14 flex gap-5 overflow-x-auto hide-scroll snap-x snap-mandatory px-6 md:px-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))] pb-4" data-lenis-prevent-horizontal>
        {items.length === 0 && <p className="text-slate-500" data-testid="events-empty">Belum ada kegiatan terdekat.</p>}
        {items.map((e, i) => (
          <Reveal key={e.id} delay={i * 0.06} className="snap-start shrink-0 w-[80vw] sm:w-[22rem] bg-white rounded-3xl overflow-hidden shadow-sm border border-slate-100 hover:-translate-y-1 transition duration-300 group" data-testid={`landing-event-${i}`}>
            <div className="relative h-52 overflow-hidden">
              <CoverImage src={fileUrl(e.image_url)} alt={e.title} className="w-full h-full" imgClassName="group-hover:scale-105 transition-transform duration-700" />
              <span className="absolute top-4 left-4"><EventBadge e={e} /></span>
              <div className="absolute top-4 right-4 bg-white rounded-2xl px-3 py-1.5 text-center shadow"><p className="text-lg font-black text-slate-900 leading-none">{fmtDate(e.start_date, { day: "2-digit" })}</p><p className="text-[10px] font-bold text-blue-600 uppercase">{fmtDate(e.start_date, { month: "short" })}</p></div>
            </div>
            <div className="p-6">
              <p className="text-lg font-extrabold text-slate-900">{e.title}</p>
              <p className="text-sm text-slate-500 mt-1 line-clamp-2">{e.description}</p>
              <p className="text-xs text-slate-500 mt-4 flex items-center gap-3"><span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{e.time} WITA</span><span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{e.location}</span></p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function DevotionBlock({ devotion, site }) {
  if (!devotion) return null;
  return (
    <section data-testid="devotion-section" className="max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-36">
      <Reveal className="relative rounded-[2rem] overflow-hidden bg-slate-900 text-white min-h-[460px] grid md:grid-cols-2">
        <img src={fileUrl(devotion.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-slate-900/90 to-indigo-900/80" />
        <div className="relative p-8 md:p-14 flex flex-col justify-between">
          <Chapter n="05" label={txt(site, "devotion_label", "Renungan Harian")} dark />
          <div className="mt-12">
            <p className="text-sm text-white/60 flex items-center gap-2"><BookOpen className="w-4 h-4" />{fmtDate(devotion.date)} · {devotion.verse}</p>
            <h3 className="mt-4 text-5xl md:text-7xl font-black tracking-tighter leading-[0.9]">{devotion.title}</h3>
            <p className="mt-6 text-slate-300 max-w-md leading-relaxed">{devotion.verse_text}</p>
            <Link to="/portal/renungan" data-testid="devotion-read-button" className="mt-8 inline-flex items-center gap-2 bg-white text-slate-900 font-bold py-3 md:py-4 px-7 rounded-full hover:bg-blue-50 active:scale-95 transition">Baca di Portal <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function groupByCategory(list) {
  const map = new Map();
  for (const k of list) {
    const cat = k.category || "Lainnya";
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat).push(k);
  }
  return Array.from(map.entries());
}

function CategoryFold({ label, items, accent, defaultOpen, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-3xl border border-slate-100 overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`} className="w-full flex items-center justify-between gap-4 bg-white hover:bg-slate-50 px-6 py-5 transition active:scale-[0.99]">
        <span className="flex items-center gap-3 min-w-0">
          <span className={`shrink-0 w-11 h-11 rounded-2xl grid place-items-center ${accent ? "bg-amber-400 text-slate-900" : "bg-slate-900 text-white"}`}><Users className="w-5 h-5" /></span>
          <span className="text-left min-w-0"><span className="block font-extrabold text-slate-900 truncate">{label}</span><span className="block text-xs text-slate-400">{items.length} kelompok</span></span>
        </span>
        <ChevronDown className={`shrink-0 w-5 h-5 text-slate-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="bg-slate-50 px-5 md:px-6 py-4 space-y-3" data-testid={`${testid}-body`}>
          {items.map((k, i) => (
            <div key={k.id} className="bg-white rounded-2xl p-4 flex items-center gap-4 border border-slate-100" data-testid={`${testid}-item-${i}`}>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-slate-900 truncate">{k.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{[k.day, k.time && `${k.time} WITA`, k.leader].filter(Boolean).join(" · ") || "Jadwal menyusul"}</p>
              </div>
              {k.area && <span className="shrink-0 text-[11px] font-bold text-slate-400">{k.area}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Komsel({ items = [], site }) {
  const rcGroups = groupByCategory(items.filter((k) => (k.type || "rc") !== "tts"));
  const ttsGroups = groupByCategory(items.filter((k) => k.type === "tts"));
  return (
    <section id="komsel" data-testid="komsel-section" className="max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-36 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <Reveal><Chapter n="06" label={txt(site, "community_label", "Relationship Community")} /></Reveal>
        <h2 className="mt-10 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[0.95]"><MaskLine inView>{txt(site, "community_heading_1", "Tidak ada yang")}</MaskLine><MaskLine inView delay={0.1} className="text-blue-600">{txt(site, "community_heading_2", "bertumbuh sendiri.")}</MaskLine></h2>
        <Reveal delay={0.1}><p className="mt-6 text-slate-500 leading-relaxed" data-testid="community-text">{txt(site, "community_text", "Relationship Community (RC) adalah keluarga kecil tempat kita saling menguatkan.")}</p></Reveal>
      </div>
      <div className="lg:col-span-7 space-y-4">
        {rcGroups.length === 0 && ttsGroups.length === 0 && <p className="text-sm text-slate-400" data-testid="komsel-empty">Kelompok komunitas segera hadir.</p>}
        {rcGroups.map(([cat, list], i) => <CategoryFold key={cat} label={cat} items={list} defaultOpen={i === 0} testid={`rc-category-${i}`} />)}
        {ttsGroups.length > 0 && (
          <>
            <Reveal className="pt-6"><p className="text-xs font-black uppercase tracking-[0.3em] text-amber-600" data-testid="tts-community-heading">TTS Community · Time To Shine</p></Reveal>
            {ttsGroups.map(([cat, list], i) => <CategoryFold key={cat} label={cat} items={list} accent defaultOpen={i === 0} testid={`tts-category-${i}`} />)}
          </>
        )}
      </div>
    </section>
  );
}

export function Gallery({ items = [], site }) {
  return (
    <section id="media" data-testid="gallery-section" className="bg-slate-900 text-white py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal><Chapter n="07" label={txt(site, "gallery_label", "Galeri Pelayanan")} dark /></Reveal>
        <h2 className="mt-10 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]"><MaskLine inView>{txt(site, "gallery_heading_1", "Momen yang")}</MaskLine><MaskLine inView delay={0.1} className="text-gradient pr-2">{txt(site, "gallery_heading_2", "membekas.")}</MaskLine></h2>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-4">
          {items.map((g, i) => (
            <Reveal key={g.id} delay={i * 0.06} className={`relative rounded-3xl overflow-hidden group ${i === 0 ? "col-span-2 row-span-2" : ""}`} data-testid={`gallery-item-${i}`}>
              <motion.img whileHover={{ scale: 1.08 }} transition={{ duration: 1, ease: EASE }} src={fileUrl(g.image_url)} alt={g.caption} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <p className="absolute bottom-4 left-5 font-bold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition duration-500">{g.caption}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const inputCls = "w-full border border-slate-200 rounded-xl p-3 md:p-4 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 bg-white transition";

export function PastoralForm({ site }) {
  const [form, setForm] = useState({ name: "", phone: "", type: "Doa", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/pastoral", form);
      toast.success("Permohonan terkirim. Tim pastoral akan menghubungi Anda.");
      setForm({ name: "", phone: "", type: "Doa", message: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <section id="pastoral" data-testid="pastoral-section" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36 grid lg:grid-cols-2 gap-12">
      <div>
        <Reveal><Chapter n="08" label={txt(site, "pastoral_label", "Layanan Pastoral")} /></Reveal>
        <h2 className="mt-10 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[0.95]"><MaskLine inView>{txt(site, "pastoral_heading_1", "Butuh dukungan")}</MaskLine><MaskLine inView delay={0.1}>{txt(site, "pastoral_heading_2", "doa?")}</MaskLine></h2>
        <Reveal delay={0.1}><p className="mt-6 text-slate-500 leading-relaxed max-w-md" data-testid="pastoral-text">{txt(site, "pastoral_text", "Kami percaya kuasa doa. Tim pastoral siap mendukung Anda.")}</p></Reveal>
      </div>
      <Reveal delay={0.1}>
        <form onSubmit={submit} className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100 space-y-4" data-testid="pastoral-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="pastoral-name-input" className={inputCls} placeholder="Nama" value={form.name} onChange={set("name")} required />
            <input data-testid="pastoral-phone-input" className={inputCls} placeholder="No. WhatsApp" value={form.phone} onChange={set("phone")} required />
          </div>
          <select data-testid="pastoral-type-select" className={inputCls} value={form.type} onChange={set("type")}>
            {["Doa", "Konseling", "Kedukaan", "Kunjungan Kasih"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <textarea data-testid="pastoral-message-input" rows={4} className={inputCls} placeholder="Ceritakan kebutuhan Anda…" value={form.message} onChange={set("message")} required />
          <button data-testid="pastoral-submit-button" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-bold py-3 md:py-4 px-6 rounded-xl shadow-md hover:bg-blue-700 transform active:scale-95 transition disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><HeartHandshake className="w-4 h-4" /> Ajukan Layanan Pastoral</>}
          </button>
        </form>
      </Reveal>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="bg-slate-900 text-white" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-10">
        <p className="text-6xl md:text-9xl font-black tracking-tighter leading-[0.85]">GIM Church<br /><span className="text-gradient pr-2">Makassar.</span></p>
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-6 text-sm text-slate-400">
          <span className="flex items-center gap-3"><Logo dark className="h-6" /><span className="text-slate-500">· Makassar, Sulawesi Selatan</span></span>
          <div className="flex items-center gap-6">
            <Link to="/portal" className="hover:text-white transition-colors" data-testid="footer-portal-link"><CalendarDays className="inline w-4 h-4 mr-1" />Portal Jemaat</Link>
            <Link to="/login" title="OPC Access" data-testid="footer-opc-link" className="hover:text-white transition-colors">✝ OPC</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
